const NON_FILTERABLE_ROW_TYPES = new Set(['header', 'subHeader', 'total'])

const toSearchText = (value) => {
  if (value == null) return ''

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : ''
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  if (Array.isArray(value)) {
    return value.map(toSearchText).filter(Boolean).join(' ')
  }

  return String(value)
}

const normalizeSearchText = (value) =>
  toSearchText(value).replace(/\s+/g, ' ').trim().toLowerCase()

const hasSearchableValue = (value) => {
  if (value == null) return false
  if (typeof value === 'string') return value.trim() !== ''
  return true
}

const getFilterValue = (filterValues, field) => normalizeSearchText(filterValues?.[field])

export const getReportRowFilterValue = (row, column) => {
  if (!row || !column?.field) return ''

  const field = column.field

  if (typeof field === 'string' && field.includes('^^')) {
    const combinedValue = row[field]
    if (combinedValue != null && combinedValue !== '') {
      return toSearchText(combinedValue)
    }

    return field
      .split('^^')
      .map((part) => row[part])
      .filter(hasSearchableValue)
      .map(toSearchText)
      .join(' ')
  }

  return toSearchText(row[field])
}

export const hasActiveContainsFilters = (filterColumns = [], filterValues = {}) =>
  filterColumns.some((column) => getFilterValue(filterValues, column.field))

export const rowMatchesContainsFilters = (row, filterColumns = [], filterValues = {}) =>
  filterColumns.every((column) => {
    const filterValue = getFilterValue(filterValues, column.field)
    if (!filterValue) return true

    const cellValue = normalizeSearchText(getReportRowFilterValue(row, column))
    if (!cellValue) return false

    return cellValue.includes(filterValue)
  })

export const syncColumnsWithDefaults = (savedCols = [], defaultCols = []) => {
  if (!Array.isArray(defaultCols) || defaultCols.length === 0) {
    return Array.isArray(savedCols) ? savedCols : []
  }
  if (!Array.isArray(savedCols) || savedCols.length === 0) {
    return defaultCols
  }

  const defaultFields = new Set(defaultCols.map((col) => col?.field).filter(Boolean))
  const merged = []
  const addedFields = new Set()

  savedCols.forEach((savedCol) => {
    if (!savedCol || !savedCol.field) return
    if (defaultFields.has(savedCol.field)) {
      const defaultCol = defaultCols.find((dc) => dc?.field === savedCol.field)
      merged.push({
        ...defaultCol,
        ...savedCol,
        header: defaultCol?.header ?? savedCol.header,
        exportHead: defaultCol?.exportHead ?? savedCol.exportHead,
        headerSx: defaultCol?.headerSx ?? savedCol.headerSx,
        containsFilter: defaultCol?.containsFilter ?? savedCol.containsFilter,
        isSelected: savedCol.isSelected !== undefined ? savedCol.isSelected : (defaultCol?.isSelected ?? true),
      })
      addedFields.add(savedCol.field)
    }
  })

  defaultCols.forEach((defaultCol, defaultIdx) => {
    if (defaultCol && defaultCol.field && !addedFields.has(defaultCol.field)) {
      let insertIndex = merged.length
      for (let i = defaultIdx - 1; i >= 0; i--) {
        const prevField = defaultCols[i]?.field
        const foundIdx = merged.findIndex((m) => m.field === prevField)
        if (foundIdx !== -1) {
          insertIndex = foundIdx + 1
          break
        }
      }

      merged.splice(insertIndex, 0, {
        ...defaultCol,
        isSelected: defaultCol.isSelected !== undefined ? defaultCol.isSelected : true,
      })
      addedFields.add(defaultCol.field)
    }
  })

  return merged
}

export const mergeContainsFilterFromDefaults = (columns = [], defaultColumns = []) => {
  const syncedCols = syncColumnsWithDefaults(columns, defaultColumns)
  const filterFields = new Set(
    defaultColumns.filter((col) => col.containsFilter).map((col) => col.field)
  )

  return syncedCols.map((col) =>
    filterFields.has(col.field) ? { ...col, containsFilter: true } : col
  )
}

export const applyContainsFilter = (tableData = [], filterColumns = [], filterValues = {}) => {
  const activeFilterColumns = filterColumns.filter((column) => column.containsFilter)

  if (!hasActiveContainsFilters(activeFilterColumns, filterValues)) {
    return tableData
  }

  return tableData.filter((row) => {
    if (NON_FILTERABLE_ROW_TYPES.has(row?.dataType)) {
      return true
    }

    return rowMatchesContainsFilters(row, activeFilterColumns, filterValues)
  })
}
